import { useI18n } from '../../../i18n';
// ============================================
// PurchaseOrdersFilterBento - 8:4 Filter Grid
// Left: Filters card with 3 dropdowns
// Right: Total Value blue KPI card
// ============================================

import { CalendarIcon, FilterIcon } from '../../../components/icons';
import { SelectDropdown } from '../../../components/shared/SelectDropdown';
import { TotalValueCardBlue } from './TotalValueCardBlue';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

interface PurchaseOrdersFilterBentoProps {
  // Filter state
  selectedSupplier: string | null;
  selectedStatus: string | null;
  dateRange: [Date | null, Date | null];
  onSupplierChange: (value: string | null) => void;
  onStatusChange: (value: string | null) => void;
  onDateRangeChange: (range: [Date | null, Date | null]) => void;
  // Options
  supplierOptions: { value: string; label: string }[];
  statusOptions: { value: string; label: string }[];
  // KPI data
  totalValue: number;
  /** Giá trị THỰC NHẬN cùng kỳ. Bỏ trống thì thẻ chỉ hiện số đặt hàng. */
  receivedValue?: number;
  valueDelta?: number;
  className?: string;
}

// ============================================
// Filter Dropdown Component
// ============================================

interface FilterDropdownProps {
  label: string;
  value: string | null;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string | null) => void;
}

function FilterDropdown({
  label,
  value,
  placeholder,
  options,
  onChange,
}: FilterDropdownProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
        {label}
      </label>
      {/* Droplist tuỳ biến (không dùng <select> native để mobile không mở popup hệ điều hành) */}
      <SelectDropdown
        value={value || ''}
        onChange={(v) => onChange(v || null)}
        options={[{ value: '', label: placeholder }, ...options]}
        className="min-w-0"
      />
    </div>
  );
}

// ============================================
// Date Range Picker (Simplified)
// ============================================

interface DateRangePickerProps {
  label: string;
  value: [Date | null, Date | null];
  onChange: (range: [Date | null, Date | null]) => void;
}

/**
 * `<input type="date">` làm việc với chuỗi `YYYY-MM-DD` KHÔNG kèm múi giờ.
 *
 * Bug #159: bản cũ đọc bằng `new Date('2026-08-19')` — JS hiểu chuỗi này là nửa
 * đêm UTC, không phải nửa đêm giờ máy — và ghi ngược bằng `toISOString()`. Ở múi
 * giờ dương như Kyiv (UTC+3), ngày người dùng chọn và ngày dùng để lọc lệch nhau
 * vài tiếng, đủ để một phiếu tạo lúc 09:00 rơi ra ngoài khoảng.
 *
 * Hai hàm dưới đây đọc/ghi thuần theo lịch địa phương nên vòng
 * chọn -> lọc -> hiển thị lại khép kín.
 */
function parseDateInput(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatDateInput(date: Date | null): string {
  if (!date) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function DateRangePicker({ label, value, onChange }: DateRangePickerProps) {
  const { t } = useI18n();
  const formatDate = formatDateInput;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
        {label}
      </label>
      <div
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-lg px-3',
          'bg-[rgba(225,226,237,0.3)]',
          'cursor-pointer',
          'focus-within:ring-2 focus-within:ring-primary-500/30'
        )}
      >
        <CalendarIcon size={16} className="text-[#6B7280]" />
        <input
          type="date"
          value={formatDate(value[0])}
          onChange={(e) => {
            const newDate = parseDateInput(e.target.value);
            onChange([newDate, value[1]]);
          }}
          className="w-[90px] bg-transparent text-sm text-[#191B23] outline-none"
          placeholder={t('start')}
        />
        <span className="text-[#6B7280]">-</span>
        <input
          type="date"
          value={formatDate(value[1])}
          onChange={(e) => {
            const newDate = parseDateInput(e.target.value);
            onChange([value[0], newDate]);
          }}
          className="w-[90px] bg-transparent text-sm text-[#191B23] outline-none"
          placeholder={t('end')}
        />
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function PurchaseOrdersFilterBento({
  selectedSupplier,
  selectedStatus,
  dateRange,
  onSupplierChange,
  onStatusChange,
  onDateRangeChange,
  supplierOptions,
  statusOptions,
  totalValue,
  receivedValue,
  valueDelta,
  className,
}: PurchaseOrdersFilterBentoProps) {
  const { t } = useI18n();
  return (
    <div className={cn('grid grid-cols-12 gap-6', className)}>
      {/* Left Card - Filters (col-span-8) */}
      <div className="col-span-12 lg:col-span-8">
        <div className="rounded-xl bg-white p-6 shadow-sm">
          {/* Filters Header */}
          <div className="mb-4 flex items-center gap-2">
            <FilterIcon size={16} className="text-[#434655]" />
            <span className="font-sans text-sm font-bold uppercase tracking-[1.4px] text-[#434655]">
              Filters
            </span>
          </div>

          {/* Filter Grid: Supplier + Status chung 1 hàng trên mobile, Date full hàng dưới */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <FilterDropdown
              label={t('purchaseordersSupplier')}
              value={selectedSupplier}
              placeholder={t('allSuppliers')}
              options={supplierOptions}
              onChange={onSupplierChange}
            />
            <FilterDropdown
              label={t('purchaseordersStatus')}
              value={selectedStatus}
              placeholder={t('allStatus')}
              options={statusOptions}
              onChange={onStatusChange}
            />
            <div className="col-span-2 sm:col-span-1">
              <DateRangePicker
                label={t('creationDate')}
                value={dateRange}
                onChange={onDateRangeChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Card - Total Value (col-span-4) */}
      <div className="col-span-12 lg:col-span-4">
        <TotalValueCardBlue
          value={totalValue}
          secondaryValue={receivedValue}
          delta={valueDelta}
          className="h-full"
        />
      </div>
    </div>
  );
}
